import { Grid, Paper, Typography, makeStyles } from "@material-ui/core";

import * as React from "react";
import { useTranslation } from "react-i18next";
import { IExtendedUser } from "src/common/types";
import { getFullName } from "src/helpers";
import Avatar from "../Avatar";
import Tenure from "./tenure";

const useStyles = makeStyles((theme: any) => ({
  avatarContainer: {
    "@media print": {
      width: "auto !important" as "auto"
    },
    margin: "0 10px 10px",
    textAlign: "center" as "center",
    width: "100%",
    [theme.breakpoints.up("sm")]: {
      margin: "0 10px 0 0",
      width: 100
    },
    [theme.breakpoints.up("lg")]: {
      margin: "0 22px 0 0"
    }
  },
  branch: {
    "@media print": {
      color: "#5d5d5d5",
      fontSize: "1rem",
      lineHeight: "1.1rem"
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.2rem"
    },
    [theme.breakpoints.up("md")]: {
      fontSize: "1.4rem"
    }
  },
  container: {
    "@media print": {
      flexWrap: "nowrap" as "nowrap",
      justifyContent: "flex-start" as "flex-start"
    },
    flexWrap: "wrap" as "wrap",
    justifyContent: "space-around" as "space-around",
    padding: "12px 8px",
    [theme.breakpoints.up("sm")]: {
      flexWrap: "nowrap" as "nowrap",
      justifyContent: "flex-start" as "flex-start",
      padding: "16px"
    }
  },
  containerWrapper: {
    minHeight: "100%",
    width: "100%"
  },
  detailsContainer: {
    "@media print": {
      width: "auto !important" as "auto"
    },
    maxWidth: "100%",
    width: "100%",
    [theme.breakpoints.up("sm")]: {
      maxWidth: "calc(100% - 110px)",
      width: "auto"
    },
    [theme.breakpoints.up("lg")]: {
      maxWidth: "calc(100% - 122px)"
    }
  },
  minimized: {
    "& $avatarContainer": {
      [theme.breakpoints.up("md")]: {
        height: "42px",
        width: "42px"
      }
    },
    "& $avatarContainer > div": {
      [theme.breakpoints.up("md")]: {
        height: "42px",
        width: "42px"
      }
    },
    "& $avatarContainer [data-avatar] > div": {
      [theme.breakpoints.up("md")]: {
        height: "42px",
        width: "42px"
      }
    },
    "& $avatarContainer [data-avatar] img": {
      [theme.breakpoints.up("md")]: {
        height: "42px",
        width: "42px"
      }
    },
    "& $branch": {
      [theme.breakpoints.up("md")]: {
        display: "none" as "none"
      }
    },
    "& $container": {
      alignItems: "center" as "center"
    },
    "& $detailsContainer": {
      [theme.breakpoints.up("md")]: {
        lineHeight: "42px",
        width: "calc(100% - 42px)"
      }
    },
    "& $name": {
      [theme.breakpoints.up("md")]: {
        color: "#545454",
        display: "inline-block" as "inline-block",
        fontSize: "2rem",
        padding: 0,
        verticalAlign: "middle"
      }
    },
    "& $paperContainer": {
      [theme.breakpoints.up("md")]: {
        boxShadow: "none" as "none"
      }
    },
    "& $tenure": {
      [theme.breakpoints.up("md")]: {
        display: "none" as "none"
      }
    },
    "& $title": {
      [theme.breakpoints.up("md")]: {
        display: "none" as "none"
      }
    }
  },
  name: {
    "@media print": {
      color: "#545454",
      fontSize: "1.2rem",
      fontWeight: 600,
      lineHeight: "1.7rem"
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.8rem"
    },
    [theme.breakpoints.up("md")]: {
      fontSize: "2.2rem"
    },
    lineHeight: "24px",
    maxWidth: "100%",
    paddingBottom: "8px",
    position: "relative" as "relative",
    wordWrap: "break-word" as "break-word"
  },
  paperContainer: {
    minHeight: "100%",
    width: "100%"
  },
  tenure: {
    "& i": {
      fontStyle: "normal" as "normal"
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.2rem"
    },
    [theme.breakpoints.up("md")]: {
      fontSize: "1.4rem"
    }
  },
  title: {
    "@media print": {
      color: "#545454",
      fontSize: "1rem",
      lineHeight: "1.4rem"
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.4rem"
    },
    [theme.breakpoints.up("md")]: {
      fontSize: "1.8rem"
    },
    lineHeight: "20px",
    marginBottom: 4
  }
}));

interface IOwnProps {
  minimize?: boolean;
}

type Props = IExtendedUser & IOwnProps;

const ProfileCard = ({
  photoBase64,
  firstName,
  lastName,
  middleName,
  title,
  department,
  subDepartment,
  tenure,
  minimize = false
}: Props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const wrapperClassNames = minimize
    ? [classes.containerWrapper, classes.minimized].join(" ")
    : classes.containerWrapper;

  const fullName = getFullName([firstName, middleName, lastName]);
  const branch = [department, subDepartment].filter(value => value).join(", ");

  return (
    <div data-qa-selector="profileCard" className={wrapperClassNames}>
      <Paper className={classes.paperContainer}>
        <Grid className={classes.container} container>
          <Grid className={classes.avatarContainer} item>
            <Avatar fullName={fullName} avatar={photoBase64} type="large" />
          </Grid>
          <Grid className={classes.detailsContainer} item>
            <Typography data-qa-selector="fullName" color="primary" className={classes.name}>
              {fullName}
            </Typography>
            <Typography data-qa-selector="title" className={classes.title}>
              {title}
            </Typography>
            <Typography data-qa-selector="branch" className={classes.branch}>
              {branch}
            </Typography>
            <Typography className={classes.tenure}>
              {t(`feedback.profileCard.tenure`)}: <i data-qa-selector="tenure">{<Tenure months={tenure} />}</i>
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </div>
  );
};

export default React.memo(ProfileCard);
